export type DeliveryChannel = 'email' | 'sms' | 'whatsapp'

// Color intent for labels on the delivery path diagram.
export type PathLabelTone = 'neutral' | 'success' | 'danger'

// One box on the delivery path.
export type DeliveryPathNode = {
  id: string
  type: 'brand' | 'mailServer' | 'mobileOperator' | 'meta' | 'recipient'
  labels: string[]
}

/**
 * Link between two path nodes.
 * Desktop shows labeled arrows; mobile shows a compact connector icon.
 */
export type DeliveryPathLink = {
  id: string
  /** Forward label (e.g. Sent / Delivered) */
  forwardLabel?: string
  forwardTone?: PathLabelTone
  /** Return / failure label (e.g. Delivered / No delivery receipt) */
  reverseLabel?: string
  reverseTone?: PathLabelTone
  /** Compact icon between nodes on small screens */
  mobileConnector: 'bidirectional' | 'bidirectionalGrayRed' | 'forward' | 'danger'
}

/** One row in the TRACKER LABEL / WHAT IT MEANS table. */
export type TrackerStatusRow = {
  label: string
  meaning: string
}

/** Full help panel for one channel tab. */
export type DeliveryHelpContent = {
  path: {
    nodes: DeliveryPathNode[]
    links: DeliveryPathLink[]
  }
  watchOut: string
  statusRows: TrackerStatusRow[]
}

export type DeliveryChannelTab = {
  id: DeliveryChannel
  label: string
}

const ASSET_BASE = 'assets/images/v2/delivery-status'

export const DELIVERY_CHANNEL_TABS: readonly DeliveryChannelTab[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'whatsapp', label: 'WhatsApp' }
] as const

export const DELIVERY_HELP_INTRO =
  'Every message passes through a service provider before it reaches the recipient. What we can report depends on what that provider sends back to us.'

export const DELIVERY_HELP_ASSETS = {
  fox: `${ASSET_BASE}/micetribe-fox.svg`,
  wordmark: `${ASSET_BASE}/micetribe-wordmark.svg`,
  mailServer: `${ASSET_BASE}/mail-server.svg`,
  mobileOperator: `${ASSET_BASE}/mobile-operator.svg`,
  meta: `${ASSET_BASE}/meta.svg`,
  recipient: `${ASSET_BASE}/recipient.svg`,
  arrowRight: `${ASSET_BASE}/arrow-right.svg`,
  arrowRightMobile: `${ASSET_BASE}/arrow-right-mobile.svg`,
  arrowLeftGreen: `${ASSET_BASE}/arrow-left-green.svg`,
  arrowLeftRed: `${ASSET_BASE}/arrow-left-red.svg`,
  lineRed: `${ASSET_BASE}/line-red.svg`,
  arrowBidirectional: `${ASSET_BASE}/arrow-bidirectional.svg`,
  arrowBidirectionalGrayRed: `${ASSET_BASE}/arrow-bidirectional-gray-red.svg`,
  arrowRightRed: `${ASSET_BASE}/arrow-right-red.svg`,
  exclamation: `${ASSET_BASE}/exclamation.svg`
} as const

const EMAIL_HELP: DeliveryHelpContent = {
  path: {
    nodes: [
      { id: 'micetribe', type: 'brand', labels: ['MICEtribe'] },
      { id: 'mail-server', type: 'mailServer', labels: ['Recipient’s', 'Mail server'] },
      { id: 'recipient', type: 'recipient', labels: ['Recipient'] }
    ],
    links: [
      {
        id: 'sent-delivered',
        forwardLabel: 'Sent',
        forwardTone: 'neutral',
        reverseLabel: 'Delivered',
        reverseTone: 'success',
        mobileConnector: 'bidirectional'
      },
      {
        id: 'not-forwarded',
        forwardLabel: 'Not forwarded (IT policy)',
        forwardTone: 'danger',
        mobileConnector: 'danger'
      }
    ]
  },
  watchOut: "Email providers can confirm when an email reaches the organization's mail server, but this doesn't always mean it reached the recipient inbox.",
  statusRows: [
    {
      label: 'Sent',
      meaning: 'The email has left MICEtribe invitation tracking service.'
    },
    {
      label: 'Delivered',
      meaning: "The organization's mail server has received the message."
    },
    {
      label: 'Bounced',
      meaning: "The email doesn't exist or unreachable."
    },
    {
      label: 'Opted out',
      meaning: 'Recipient has opted out from receiving emails from your organization.'
    }
  ]
}

const SMS_HELP: DeliveryHelpContent = {
  path: {
    nodes: [
      { id: 'micetribe', type: 'brand', labels: ['MICEtribe'] },
      { id: 'mobile-operator', type: 'mobileOperator', labels: ['Mobile operator'] },
      { id: 'recipient', type: 'recipient', labels: ['Recipient'] }
    ],
    links: [
      {
        id: 'sent',
        forwardLabel: 'Sent',
        forwardTone: 'neutral',
        mobileConnector: 'forward'
      },
      {
        id: 'delivered-or-no-receipt',
        forwardLabel: 'Delivered',
        forwardTone: 'neutral',
        reverseLabel: 'No delivery receipt',
        reverseTone: 'danger',
        mobileConnector: 'bidirectionalGrayRed'
      }
    ]
  },
  watchOut:
    "SMS delivery confirmation depends on the mobile network operator, and the preference of the recipient whether they allow delivery receipts or not. We can confirm when a message is sent, but we may not be able to confirm that it reached the recipient's mobile.",
  statusRows: [
    {
      label: 'Sent',
      meaning: 'The SMS has left MICEtribe invitation tracking service.'
    },
    {
      label: 'Delivered',
      meaning: 'The SMS was delivered to the recipient mobile.'
    },
    {
      label: 'Failed',
      meaning: "The phone number doesn't exist or unreachable."
    }
  ]
}

const WHATSAPP_HELP: DeliveryHelpContent = {
  path: {
    nodes: [
      { id: 'micetribe', type: 'brand', labels: ['MICEtribe'] },
      { id: 'meta', type: 'meta', labels: ['Meta'] },
      { id: 'recipient', type: 'recipient', labels: ['Recipient'] }
    ],
    links: [
      {
        id: 'sent',
        forwardLabel: 'Sent',
        forwardTone: 'neutral',
        mobileConnector: 'forward'
      },
      {
        id: 'delivered-or-no-receipt',
        forwardLabel: 'Delivered',
        forwardTone: 'neutral',
        reverseLabel: 'No delivery receipt',
        reverseTone: 'danger',
        mobileConnector: 'bidirectionalGrayRed'
      }
    ]
  },
  watchOut:
    "Message delivery confirmation depends on Meta, and the preference of the recipient whether they allow delivery receipts or not. We can confirm when a message is sent, but we may not be able to confirm that it reached the recipient's inbox.",
  statusRows: [
    {
      label: 'Sent',
      meaning: 'The message has left MICEtribe invitation tracking service.'
    },
    {
      label: 'Delivered',
      meaning: 'Meta has received the message.'
    },
    {
      label: 'Failed',
      meaning: 'The message could not be delivered due to an invalid number, recipient restrictions, or a temporary delivery issue.'
    }
  ]
}

export const DELIVERY_HELP_BY_CHANNEL: Record<DeliveryChannel, DeliveryHelpContent> = {
  email: EMAIL_HELP,
  sms: SMS_HELP,
  whatsapp: WHATSAPP_HELP
}
